// Maple St. demo fixtures — the SHARED constants behind the calc preview
// harness AND the Phase-1 help articles.
//
// Help articles state the same numbers the helpshot captures show. Instead of
// trusting two hand-typed copies to agree forever, both sides import THIS
// module: CalcPreviewHarness seeds its mocks from these constants, and the
// articles' `values` tables read them directly, so a fixture change re-renders
// the article and invalidates the committed capture (helpshots:check) in the
// same commit. Pure data — no React, no imports — so the help:index bundling
// (esbuild over the content graph) stays trivial.

/** The demo calc project every capture is taken in. */
export const MAPLE_PROJECT = {
  name: "Maple St. Residence",
  address: "418 Maple St, Brookline MA",
  jobNumber: "FK-2026-0418",
  client: "Brookline Builders LLC",
  engineer: "Jonathan Haberer, P.E.",
  license: "MA 51234",
  buildingCode: "IBC 2024",
  riskCategory: "II",
  designMethod: "ASD",
} as const;

/** lb → plf at a link's spacing (what the transfer preview prints). */
export function lineTransferPlf(lb: number, spacingIn: number): number {
  return lb / (spacingIn / 12);
}

function fixtureNumber(value: number, digits = 3): number {
  return Number(value.toFixed(digits));
}

/* One coherent FJ-1 design record. Every member, load-link, result, and
 * reaction fixture below is derived from this object instead of carrying an
 * independent teaching number. */
const fj1SpanFt = 14;
const fj1SpanIn = fj1SpanFt * 12;
const fj1SpacingIn = 16;
const fj1DirectAreaPsf = { D: 15, L: 40 } as const;
const fj1DirectLinePlf = {
  D: fixtureNumber(fj1DirectAreaPsf.D * fj1SpacingIn / 12),
  L: fixtureNumber(fj1DirectAreaPsf.L * fj1SpacingIn / 12),
} as const;
const fj1ReactionPacketLb = { D: 600, L: 380 } as const;
const fj1InheritedLinePlf = {
  D: fixtureNumber(lineTransferPlf(fj1ReactionPacketLb.D, fj1SpacingIn)),
  L: fixtureNumber(lineTransferPlf(fj1ReactionPacketLb.L, fj1SpacingIn)),
} as const;
const fj1LineTotalsPlf = {
  D: fixtureNumber(fj1DirectLinePlf.D + fj1InheritedLinePlf.D),
  L: fixtureNumber(fj1DirectLinePlf.L + fj1InheritedLinePlf.L),
  total: fixtureNumber(
    fj1DirectLinePlf.D + fj1InheritedLinePlf.D
      + fj1DirectLinePlf.L + fj1InheritedLinePlf.L,
  ),
} as const;
const fj1ReactionLb = fixtureNumber(fj1LineTotalsPlf.total * fj1SpanFt / 2);
const fj1MomentLbFt = fixtureNumber(fj1LineTotalsPlf.total * fj1SpanFt ** 2 / 8);
const fj1LiveDeflectionLimitIn = fixtureNumber(fj1SpanIn / 360);
const fj1LongTermDeflectionLimitIn = fixtureNumber(fj1SpanIn / 240);
const fj1Checks = [
  { key: "bending", label: "Bending", demandSymbol: "M", capacitySymbol: "M'", demand: fj1MomentLbFt, capacity: fixtureNumber(fj1MomentLbFt / 0.64), units: "lb-ft", utilization: 0.64, combo: "D+L" },
  { key: "shear", label: "Shear", demandSymbol: "V", capacitySymbol: "V'", demand: fj1ReactionLb, capacity: fixtureNumber(fj1ReactionLb / 0.46), units: "lb", utilization: 0.46, combo: "D+L" },
  { key: "bearing", label: "Bearing", demandSymbol: "R", capacitySymbol: "R'", demand: fj1ReactionLb, capacity: fixtureNumber(fj1ReactionLb / 0.32), units: "lb", utilization: 0.32, combo: "D+L" },
  { key: "deflection_live", label: "Deflection (LL)", demandSymbol: "Δ", capacitySymbol: "Δ_allow", demand: fixtureNumber(fj1LiveDeflectionLimitIn * 0.72), capacity: fj1LiveDeflectionLimitIn, units: "in", utilization: 0.72, combo: "L" },
  { key: "deflection_long_term", label: "Long-term deflection", demandSymbol: "δT", capacitySymbol: "Δ", demand: fixtureNumber(fj1LongTermDeflectionLimitIn * 0.694), capacity: fj1LongTermDeflectionLimitIn, units: "in", utilization: 0.694, combo: "D+L" },
] as const;
const fj1Governing = {
  key: fj1Checks[3].key,
  label: fj1Checks[3].label,
  utilization: fj1Checks[3].utilization,
} as const;

export const FJ1_SHEET = {
  mark: "FJ-1",
  role: "floor_joist",
  section: {
    size: "2x10",
    species: "Southern Pine",
    grade: "No. 2",
    source: "catalog",
  },
  geometry: {
    spanFt: fj1SpanFt,
    spanIn: fj1SpanIn,
    spacingIn: fj1SpacingIn,
    bearingIn: 1.5,
    deflectionLiveRatio: 360,
    deflectionTotalRatio: 240,
  },
  directAreaPsf: fj1DirectAreaPsf,
  directLinePlf: fj1DirectLinePlf,
  inherited: {
    sourceMark: "B-1",
    support: "left",
    byType: fj1ReactionPacketLb,
    spacingIn: fj1SpacingIn,
    envelopeLb: fj1ReactionPacketLb.D + fj1ReactionPacketLb.L,
    lineByType: fj1InheritedLinePlf,
  },
  lineTotalsPlf: fj1LineTotalsPlf,
  demands: {
    momentLbFt: fj1MomentLbFt,
    shearLb: fj1ReactionLb,
    reactionLb: fj1ReactionLb,
  },
  reactions: {
    leftLb: fj1ReactionLb,
    rightLb: fj1ReactionLb,
  },
  checks: fj1Checks,
  governing: fj1Governing,
  minBearingIn: fixtureNumber(
    fj1ReactionLb / (fj1Checks[2].capacity / 1.5),
  ),
} as const;

/** Compatibility aliases keep existing published article imports on the same
 * single design record. */
export const FJ1_CHECKS = FJ1_SHEET.checks;
export const FJ1_GOVERNING = FJ1_SHEET.governing;
export const FJ1_REACTION_LB = FJ1_SHEET.demands.reactionLb;
export const FJ1_MIN_BEARING_IN = FJ1_SHEET.minBearingIn;

/** Member-picker teaching rows (scored candidate sweep on FJ-1's loads). */
export const PICKER_FAIL_2X10 = { size: "2x10", species: "Douglas Fir-Larch", grade: "No. 1", maxDcr: 1.04 } as const;
/** What Autosize returns: the lightest passing catalog section. */
export const AUTOSIZE_PICK = { size: "2x10", species: "Douglas Fir-Larch", grade: "No. 2", maxDcr: 0.96 } as const;
export const PICKER_PASS_2X12 = { size: "2x12", species: "Douglas Fir-Larch", grade: "No. 2", maxDcr: 0.63 } as const;

/** SB-1's schema-driven steel record. The harness uses these values for both
 * the saved inputs and the engine-shaped result shown in help captures. */
export const STEEL_BEAM_SHEET = {
  mark: "SB-1",
  shapeKey: "W18X50",
  gradeKey: "a992",
  gradeLabel: "ASTM A992",
  designMethod: "ASD",
  unbracedLengthFt: 0,
  momentGradientCb: 1,
  requiredMomentKipFt: 184,
  momentCapacityKipFt: 252,
  momentDcr: 0.73,
  requiredShearKip: 30,
  shearCapacityKip: 96.7,
  shearDcr: 0.31,
  standard: "ANSI/AISC 360-22",
} as const;
/** The demo load link is the inherited packet inside FJ-1's design record. */
export const LINK_B1_FJ1 = FJ1_SHEET.inherited;

/** Wave-3 wood-member teaching fixtures. These values are direct snapshots of
 * engine.calc_runtime.compute outputs; article value tables and preview
 * results both consume them so the prose cannot drift from the captures. */
export const W3_GEOMETRY = {
  sheetId: "w3-geometry",
  mark: "B-3",
  section: "3-ply 2x12",
  species: "Southern Pine",
  grade: "No. 2",
  memberLengthFt: 23,
  supports: [
    { type: "pin", pos: 0, bearing: 3, reactionLb: 770.2, minBearingIn: 0.303 },
    { type: "roller", pos: 10, bearing: 5.5, reactionLb: 2209.5, minBearingIn: 0.869 },
    { type: "roller", pos: 20, bearing: 3, reactionLb: 1391.3, minBearingIn: 0.547 },
  ],
  loads: [
    { name: "Dead", type: "D", mag: 60 },
    { name: "Snow", type: "S", mag: 120 },
  ],
  governing: { label: "Flexure", utilization: 0.244, demand: 2182.5, capacity: 8958.5, combo: "D+S" },
} as const;

export const W3_LOADS = {
  sheetId: "w3-loads",
  mark: "R-3",
  section: "2-ply 2x12",
  species: "Southern Pine",
  grade: "No. 2",
  spanFt: 12,
  loads: [
    { name: "Dead", type: "D", mag: 30, linePlf: 60, geometry: "full span" },
    { name: "Snow at ridge", type: "S", mag: 1000, geometry: "point", pos: 6 },
    { name: "Roof live", type: "Lr", mag: 40, linePlf: 80, geometry: "partial", start: 0, end: 6 },
  ],
  governing: { label: "Flexure", utilization: 0.673, demand: 4080, capacity: 6064.5, combo: "D+S" },
  reactions: [
    { label: "Left", reactionLb: 860, minBearingIn: 0.507, byType: { D: 360, Lr: 360, S: 500 } },
    { label: "Right", reactionLb: 860, minBearingIn: 0.507, byType: { D: 360, Lr: 120, S: 500 } },
  ],
} as const;

export const W3_CONDITIONS = {
  sheetId: "w3-conditions",
  mark: "FJ-3",
  section: "2x10",
  species: "Southern Pine",
  grade: "No. 2",
  spanFt: 12,
  spacingIn: 16,
  loadsPsf: { D: 15, L: 40 },
  settings: {
    designMethod: "ASD",
    service: "dry",
    temperature: "le_100F",
    incised: false,
    repetitive: true,
    selfWeight: true,
    liveRatio: 360,
    totalRatio: 240,
    longTermRatio: 240,
    kcr: 1.5,
  },
  governing: { label: "Flexure", utilization: 0.674, demand: 1382.4, capacity: 2049.9, combo: "D+L" },
  factors: [
    { symbol: "CF", value: 1 },
    { symbol: "CM", value: 1 },
    { symbol: "Ct", value: 1 },
    { symbol: "Ci", value: 1 },
    { symbol: "Cfu", value: 1 },
    { symbol: "Cr", value: 1.15 },
    { symbol: "CL", value: 1 },
    { symbol: "Cb", value: 1 },
    { symbol: "CD", value: 1 },
  ],
} as const;

export const W3_PICKER = {
  sheetId: "w3-picker",
  mark: "FJ-4",
  current: { size: "2x10", species: "Southern Pine", grade: "No. 2" },
  candidates: [
    { size: "2x6", bIn: 1.5, dIn: 5.5, areaIn2: 8.25, maxDcr: 2.308, governing: "deflectionLong", passes: false, dcrs: { flexure: 1.8726, shear: 0.47, bearing: 0.1779, deflLive: 2.1366, deflTotal: 2.0136, deflLong: 2.3082 } },
    { size: "2x8", bIn: 1.5, dIn: 7.25, areaIn2: 10.88, maxDcr: 1.175, governing: "flexure", passes: false, dcrs: { flexure: 1.1752, shear: 0.3597, bearing: 0.1795, deflLive: 0.9328, deflTotal: 0.8868, deflLong: 1.0192 } },
    { size: "2x10", bIn: 1.5, dIn: 9.25, areaIn2: 13.88, maxDcr: 0.843, governing: "flexure", passes: true, dcrs: { flexure: 0.843, shear: 0.2847, bearing: 0.1812, deflLive: 0.4491, deflTotal: 0.4312, deflLong: 0.4971 } },
    { size: "2x12", bIn: 1.5, dIn: 11.25, areaIn2: 16.88, maxDcr: 0.614, governing: "flexure", passes: true, dcrs: { flexure: 0.6138, shear: 0.2363, bearing: 0.183, deflLive: 0.2497, deflTotal: 0.242, deflLong: 0.2798 } },
  ],
  autosize: { size: "2x8", species: "Douglas Fir-Larch", grade: "Select Structural", maxDcr: 0.751, governing: "deflectionLong" },
} as const;

export const W3_RESULTS = {
  sheetId: "w3-results",
  mark: "B-4",
  section: "3-ply 2x10",
  species: "Southern Pine",
  grade: "No. 2",
  spanFt: 10,
  loads: { D: 120, L: 240 },
  checks: [
    { key: "flexure", label: "Flexure", demand: 4500, capacity: 5347.7, units: "lb-ft", utilization: 0.841, combo: "D+L" },
    { key: "shear", label: "Shear", demand: 1800, capacity: 4856.2, units: "lb", utilization: 0.371, combo: "D+L" },
    { key: "bearing", label: "Bearing", demand: 1800, capacity: 7627.5, units: "lb", utilization: 0.236, combo: "D+L" },
    { key: "deflection_live", label: "Deflection (LL)", demand: 0.1, capacity: 0.3, units: "in", utilization: 0.39, combo: "L" },
    { key: "deflection_total", label: "Deflection (total)", demand: 0.2, capacity: 0.5, units: "in", utilization: 0.39, combo: "D+L" },
    { key: "deflection_long_term", label: "Long-term deflection", demand: 0.2, capacity: 0.5, units: "in", utilization: 0.455, combo: "D+L" },
  ],
  reactions: [
    { label: "Left", reactionLb: 1800, minBearingIn: 0.708, byType: { D: 600, L: 1200 } },
    { label: "Right", reactionLb: 1800, minBearingIn: 0.708, byType: { D: 600, L: 1200 } },
  ],
} as const;
/** R-1's left support packet — the defaults-table worked example a dowel
 *  connection consumes (gravity download plus wind uplift). */
export const RAFTER_PACKET = {
  mark: "R-1",
  support: "left",
  byType: { D: 322, S: 486, W_up: -212 },
} as const;

/** CFS joist design record: unfactored-only per-end reactions (no envelope). */
export const CFS_JOIST_REACTIONS = { D: 60, L: 120 } as const;

/** The concrete beam's real reaction-packet refusal (concrete.py, verbatim —
 *  the harness serves it and the linking article quotes it). */
export const CONCRETE_REACTION_REFUSAL =
  "Demands are entered as pre-factored moment and shear, and the calculator "
  + "carries no span or unfactored load model, so support reactions cannot "
  + "be decomposed by load type.";

/** Connection sheets in the escalation story. */
export const CN1 = { mark: "CN-1", schedule: "4 nails", maxDcr: 0.62 } as const;
export const CN2 = { mark: "CN-2", schedule: "3 toe-nails", maxDcr: 1.42, governingLabel: "Lateral connection" } as const;

/** The engine's derived auto-schedule for the registry-default rafter→plate
 *  toe-nail geometry (connection_schedule.py v1 — mirrored by the harness). */
export const AUTO_SCHEDULE_DEMO = {
  summary: "3 × 16d toe-nails — max viable, governed by in-row spacing 15D",
  nailDiameterIn: 0.162,
  holdingWidthIn: 3.5,
  contactLengthIn: 3.5,
  sideWidthIn: 1.5,
  distancesIn: { end: 2.43, edge: 0.405, inRow: 2.43, rowSpacing: 0.81 },
  perRow: [2, 1],
  nFasteners: 3,
} as const;

/** The published capacity row the hardware picker serves in the harness. */
export const LUSI26_CAPACITY = {
  name: "Simpson Strong-Tie LUSI26 Joist Hanger",
  capacityLb: 1185,
  direction: "download",
  designMethod: "ASD",
  loadDurationCd: 1,
  installationLabel: "Face mount · full nail schedule",
  woodSpeciesGroup: "SPF/HF",
  fastenerSchedule: "10d common nails per published schedule",
  sourceDocument: "Connector catalog preview",
  sourceTable: "Joist hangers",
} as const;

/** Manual load-sheet fixtures used by the load-workflow help captures. These
 * values are the engine outputs asserted in test_manual_load_calculators.py;
 * the articles and the visual harness both import this module so captions and
 * captures cannot drift independently. */
export const DEAD_GRAVITY_HELP_FIXTURE = {
  mark: "DG-1",
  inputs: {
    regionLabel: "Floor A",
    surfaceKind: "Floor",
    length_ft: 28,
    width_ft: 18,
    level: "Level 2",
    structure_psf: 4,
    finish_psf: 3,
    ceiling_psf: 2,
    mep_psf: 1,
    partition_psf: 0,
    tributaryMode: "Manual region",
    includeInWeight: true,
  },
  result: {
    status: "complete" as const,
    governingLabel: "Floor A dead load",
    governingValue: "10.00 psf",
    tables: {
      "gravity-regions": [{
        region: "Floor A",
        level: "Level 2",
        kind: "Floor",
        area: 504,
        dead: 10,
        weight: 5040,
        seismicWeight: "included",
        source: "Manual assembly",
      }],
    },
    intermediates: {
      "Assembly component sum": "10.00 psf governing",
      "Region area": "504.00 ft2",
      "Total region weight": "5040.0 lb",
      "Tributary load routes": "Manual region",
      "Seismic-weight treatment": "Floor A: included",
    },
    warnings: [],
    assumptions: ["Entered assembly components and regions are nominal, unfactored dead loads."],
    deferred: [],
    notes: [],
    sources: ["ASCE/SEI 7-22 Chapter 3", "Manual assembly inputs"],
  },
};

export const RAIN_ICE_HELP_FIXTURE = {
  mark: "RI-1",
  inputs: {
    regionLabel: "Low roof",
    length_ft: 36,
    width_ft: 24,
    slope_percent: 2,
    drainCount: 2,
    overflowCount: 2,
    riskCategory: "II",
    rainfallIntensity15Min_in_per_hr: 5,
    ds_in: 1.5,
    dh_in: 1,
    dp_in: 0,
    pondingAnalysisComplete: false,
    iceApplicable: false,
    nominalThickness_in: 0,
    iceDensity_pcf: 57,
    iceImportance: 1,
  },
  result: {
    status: "partial" as const,
    governingLabel: "Low roof rain",
    governingValue: "13.00 psf",
    tables: {
      environmental: [
        { region: "Low roof", condition: "Rain", pressure: 13, depth: 2.5, status: "computed", source: "Manual drainage heads; 5 in/hr, Risk II" },
        { region: "Low roof", condition: "Ponding", pressure: null, depth: null, status: "review required", source: "Slope screen" },
        { region: "Low roof", condition: "Atmospheric ice", pressure: null, depth: null, status: "not applicable", source: "User applicability" },
      ],
    },
    intermediates: {
      "Design rainfall intensity": "5 in/hr (Risk II)",
      "Static water head": "1.50 in",
      "Hydraulic head": "1.00 in",
      "Ponding head": "Not calculated",
      "Rain pressure R": "13.00 psf",
      "Ponding applicability": "review required",
      "Design ice thickness": null,
    },
    warnings: [],
    assumptions: [],
    deferred: ["Structural ponding stability and drainage capacity require a separate system check."],
    notes: [],
    sources: ["ASCE/SEI 7-22 Chapters 8 and 10", "engine.loading_engine.stage5_policy"],
  },
};

export const WALL_ASSEMBLY_HELP_FIXTURE = {
  mark: "WG-1",
  storedLayers: [
    {
      role: "wall_sheathing",
      productKey: "gp_densglass_5_8x4x8",
      coverageFactor: 1,
    },
    {
      role: "framing",
      name: "Service blocking allowance",
      manualWeightPsf: 1.25,
      coverageFactor: 3,
    },
  ],
  resolvedLayers: [
    {
      role: "wall_sheathing",
      product: "Georgia-Pacific DensGlass Exterior Wall Sheathing 5/8 in. x 4 ft. x 8 ft.",
      source: "ceiling_wall_product_catalog",
      coverageFactor: 1,
      weight: 2.5,
    },
    {
      role: "framing",
      product: "Service blocking allowance",
      source: "engineer_manual_input",
      coverageFactor: 3,
      weight: 3.75,
    },
  ],
  faceWeightPsf: 6.25,
  lineLoadPlf: 56.25,
  inputs: {
    wallLabel: "North wall",
    level: "Level 2",
    wallType: "Exterior wood frame",
    length_ft: 30,
    heightLeft_ft: 9,
    heightRight_ft: 9,
    unitWeight_psf: 6.25,
    openingArea_sqft: 0,
    openings: [],
    minimum_plf_per_ft: 5,
    maximum_plf_per_ft: 100,
    tapered: false,
  },
  result: {
    status: "complete" as const,
    governingLabel: "North wall high-end line load",
    governingValue: "56.25 plf",
    tables: {
      walls: [{
        wall: "North wall",
        level: "Level 2",
        construction: "Exterior wood frame",
        profile: "uniform",
        length: 30,
        average: 56.25,
        top: 56.25,
        base: 56.25,
      }],
      assembly: [
        { role: "wall_sheathing", product: "Georgia-Pacific DensGlass Exterior Wall Sheathing 5/8 in. x 4 ft. x 8 ft.", source: "ceiling_wall_product_catalog", coverageFactor: 1, weight: 2.5 },
        { role: "framing", product: "Service blocking allowance", source: "engineer_manual_input", coverageFactor: 3, weight: 3.75 },
      ],
    },
    intermediates: {
      "Gross wall-face area": "270.00 ft2",
      "Opening deduction": "0.00 ft2",
      "Net wall weight": "1687.5 lb",
      "Low- and high-end line-load distribution": "56.25 / 56.25 plf",
      "Policy limit checks": "5 to 100 plf per ft of wall height",
      "Wall assembly face weight": "6.250 psf from 2 resolved layers",
    },
    warnings: [],
    assumptions: [
      "Opening area is distributed proportionally over the wall because local opening coordinates were not supplied.",
      "Wall face weight is the sum of the server-resolved assembly layer ledger.",
    ],
    deferred: [],
    notes: [],
    sources: [
      "ASCE/SEI 7-22 Chapter 3",
      "engine.calc_engine.wall_gravity_load_generator",
      "ceiling_wall_product_catalog",
    ],
  },
};

/** Exact engine fixture for the connection-check anatomy article. Values are
 * anchored in dxf-engineer/tests/test_calc_runtime.py::WoodBoltConnectionTests. */
export const CONNECTION_BOLT_DEMO = {
  sheetId: "cb2",
  mark: "CB-2",
  diameterIn: 0.5,
  fybPsi: 45_000,
  species: "Douglas Fir-Larch",
  mainThicknessIn: 1.5,
  sideThicknessIn: 1.5,
  nFasteners: 4,
  nInRow: 4,
  spacingIn: 3,
  emPsi: 1_700_000,
  mainAreaIn2: 8.25,
  sideAreaIn2: 8.25,
  loadComponentsLb: { D: 300, S: 600 },
  governingCombination: "D+S",
  demandLb: 900,
  capacityLb: 2_193.5,
  dcr: 0.410,
  zRefLbPerFastener: 483.2,
  governingYieldMode: "II",
  groupActionFactor: 0.9867,
  loadDurationFactor: 1.15,
} as const;

/** Exact selected-capacity fixture from
 * dxf-engineer/tests/test_connector_product_capacity.py. */
export const PRODUCT_CAPACITY_DEMO = {
  sheetId: "pc2",
  mark: "PC-2",
  productName: "Test Manufacturer H1",
  designation: "H1",
  capacityKey: "cap:download:1.0",
  capacitySetId: "installation:test",
  installationKey: "test:h1:install",
  direction: "download",
  designMethod: "ASD",
  loadDurationCd: 1,
  demandLb: 800,
  capacityLb: 1_000,
  dcr: 0.8,
  installationCondition: "DF/SP exact installation",
  fastenerSchedule: "Header 4-10d; joist 4-10d",
  sourceDocument: "ESR-TEST",
  sourceTable: "Table 1",
  sourcePage: "4",
} as const;

/** Complete scalar proprietary-hanger example used only by the picker
 * capture. It is the LUS24 verification row exercised by the engine tests. */
export const PROPRIETARY_HANGER_DEMO = {
  stableKey: "simpson:lus24:df_sp:hdr-4-10d-jst-2-10d",
  primary: "Simpson Strong-Tie LUS24",
  secondary: "DF/SP face-mount installation",
  installation: "Header 4-10d; joist 2-10d",
  downloadCapacityLb: 670,
  upliftCapacityLb: 435,
  condition: "DF/SP, G >= 0.50",
  sourceDocument: "ICC-ES ESR-2552",
  sourceTable: "Table 6",
  sourcePage: "14",
  sourceUrl: "https://icc-es.org/wp-content/uploads/report-directory/ESR-2552.pdf",
} as const;
